import { KCheckbox, KText } from '@/ui-library/components';
import { Flex, Tooltip } from '@chakra-ui/react';
import { useEffect, useState, useRef } from 'react'; // Import useRef
import { createPortal } from 'react-dom';

export const SchTopLeftCheckbox: React.FC<{
  checked: boolean;
  isDisabled: boolean;
  onChange: () => void;
  isIndeterminate?: boolean;
  interviewDataRef: any;
  currentWeekRange: string;
  timezone: string;
}> = ({
  checked,
  onChange,
  isIndeterminate,
  isDisabled,
  interviewDataRef,
  currentWeekRange,
  timezone,
}) => {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const prevContainerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const oldContainer = container;
    prevContainerRef.current = oldContainer;

    const timeout = setTimeout(() => {
      const cell = document.querySelector(
        '.fc-timegrid-axis-frame'
      ) as HTMLElement;
      if (cell) {
        setContainer(cell);
        cell.style.width = '100%';
        cell.style.height = '100%';
        const parentTh = cell.closest('.fc-timegrid-axis') as HTMLElement;
        if (parentTh) {
          parentTh.removeAttribute('aria-hidden');
        }
      } else {
        // If cell not found (e.g., during rapid transitions), reset container
        setContainer(null);
      }
    }, 100); // Keep delay to allow FullCalendar DOM to settle

    // Cleanup function
    return () => {
      clearTimeout(timeout);
      // Use the container from the previous render (captured in prevContainerRef) for cleanup
      const containerToClean = prevContainerRef.current;
      if (containerToClean) {
        const parentTh = containerToClean.closest(
          '.fc-timegrid-axis'
        ) as HTMLElement;
        if (parentTh && !parentTh.hasAttribute('aria-hidden')) {
          parentTh.setAttribute('aria-hidden', 'true');
        }
        // Optionally reset styles if needed, though usually handled by FullCalendar remount
        // containerToClean.style.width = '';
        // containerToClean.style.height = '';
      }
      // Reset prev container ref after cleanup
      prevContainerRef.current = null;
    };
    // ADD timezone to dependency array
  }, [container, timezone]); // <-- ADD DEPENDENCY

  // If container is not found (yet or anymore), render nothing
  if (!container) return null;

  // Check week availability
  const { weekAvailability, interviewers } = interviewDataRef?.current ?? {};
  const isWeekUnavailable = weekAvailability?.[currentWeekRange] === false;
  const finalIsDisabled = isDisabled || isWeekUnavailable;

  const noInterviewers = interviewers.length === 0;

  let disabledMessage = '';

  if (noInterviewers) {
    disabledMessage = 'Pick an interviewer to select slots';
  } else if (isWeekUnavailable) {
    disabledMessage = 'No days available in this week';
  } else if (isDisabled) {
    // Add a generic disabled message if it's disabled for other reasons (like no available slots)
    disabledMessage = 'No available slots to select';
  }

  return createPortal(
    <Flex
      w="100%"
      h="100%"
      gap="6px"
      flexDir="column"
      justifyContent="center"
      alignItems="center"
    >
      <Tooltip
        label={finalIsDisabled ? disabledMessage : ''}
        isDisabled={!finalIsDisabled}
      >
        <span>
          <KCheckbox
            isChecked={checked}
            isDisabled={finalIsDisabled}
            isIndeterminate={isIndeterminate}
            onChange={onChange}
          />
        </span>
      </Tooltip>
      <KText variant="overhead" color="secondary-700">
        ALL
      </KText>
    </Flex>,
    container
  );
};
